import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft } from "lucide-react";
import { APP_NAME } from "@/App";

const Pricing = () => {
  const navigate = useNavigate();
  const logoUrl = "https://yourpersonalshopper-storage.sfo3.digitaloceanspaces.com/assets/bo_logo.webp";

  // Update page title
  useEffect(() => {
    document.title = `Pricing - ${APP_NAME}`;
  }, []);

  const features = [
    "Unlimited client intake sessions",
    "Unlimited AI-generated summaries",
    "Custom shop profile",
    "Instantly auto-generate detailed service sheets",
    "Track + manage every job in one dashboard",
    "Your entire business — centralized & accessible",
    "Captures and structures client requests for you",
    "Smart AI intake replaces back-and-forth messaging",
    "Google Calendar integration",
    "Priority support"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1a1f] via-[#005f6b] to-[#0a1a1f] pb-8">
      {/* Navigation */}
      <nav className="px-6 py-4 flex justify-between items-center max-w-7xl mx-auto border-b border-[#005f6b]/20 backdrop-blur-sm">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
          <img src={logoUrl} alt="Logo" className="w-12 h-12 rounded-full" />
          <span className="text-xl font-semibold text-[#d4af37]">BackOffice</span>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid="pricing-login-btn"
            variant="ghost"
            onClick={() => navigate("/login")}
            className="text-[#f8f8f8] hover:text-[#00ced1] hover:bg-[#005f6b]/20"
          >
            Login
          </Button>
        </div>
      </nav>

      {/* Pricing Section */}
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-sm font-medium text-[#d4af37] bg-[#d4af37]/10 px-4 py-2 rounded-full border border-[#d4af37]/30">
              7-Day Free Trial • No Credit Card Required
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold text-[#f8f8f8] mb-4">Simple, Transparent Pricing</h1>
          <p className="text-lg text-[#f8f8f8]/70 max-w-2xl mx-auto">
            Choose the plan that works best for your business
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Monthly Plan */}
          <div className="bg-[#0a1a1f]/80 backdrop-blur-sm rounded-2xl border border-[#d4af37]/30 p-8">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-semibold text-[#f8f8f8] mb-2">Monthly Plan</h3>
              <div className="flex items-baseline justify-center gap-2 mb-4">
                <span className="text-5xl font-bold text-[#00ced1]">$24.99</span>
                <span className="text-[#f8f8f8]/60">/month</span>
              </div>
              <p className="text-sm text-[#f8f8f8]/60">Cancel anytime</p>
            </div>

            <div className="space-y-3 mb-8">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#00ced1] flex-shrink-0 mt-0.5" />
                  <span className="text-[#f8f8f8]/80">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              data-testid="monthly-plan-btn"
              onClick={() => navigate("/signup?plan=monthly")}
              className="w-full bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl"
            >
              Start Free Trial
            </Button>
          </div>

          {/* Annual Plan */}
          <div className="bg-gradient-to-br from-[#005f6b] to-[#00ced1] rounded-2xl border-2 border-[#d4af37] p-8 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-[#d4af37] text-[#0a1a1f] px-4 py-1 rounded-full text-sm font-semibold">
                Save ~$50
              </span>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-semibold text-[#f8f8f8] mb-2">Annual Plan</h3>
              <div className="flex items-baseline justify-center gap-2 mb-4">
                <span className="text-5xl font-bold text-[#f8f8f8]">$249</span>
                <span className="text-[#f8f8f8]/80">/year</span>
              </div>
              <p className="text-sm text-[#f8f8f8]/80">Best value • 2 months free</p>
            </div>

            <div className="space-y-3 mb-8">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#f8f8f8] flex-shrink-0 mt-0.5" />
                  <span className="text-[#f8f8f8]">{feature}</span>
                </div>
              ))}
            </div>

            <Button
              data-testid="annual-plan-btn"
              onClick={() => navigate("/signup?plan=yearly")}
              className="w-full bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#0a1a1f] font-semibold py-6 rounded-xl"
            >
              Start Free Trial
            </Button>
          </div>
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-16 text-center">
          <p className="text-[#f8f8f8]/70 mb-4">
            All plans include a 7-day free trial. No credit card required to start.
          </p>
          <p className="text-[#f8f8f8]/60 text-sm">
            Questions? Contact us at support@backoffice.ai
          </p>
        </div>
      </div>

      <footer className="text-center text-[#f8f8f8]/70 mt-12 text-sm">
        <p>© 2025 BackOffice. All rights reserved. <a className="cursor-pointer text-[#d4af37] hover:text-[#00ced1]" onClick={() => navigate("/privacy-policy")}>Privacy Policy</a></p>
      </footer>
    </div>
  );
};

export default Pricing;
